// create plan form 25/1/2024
import React, { useState, useRef } from "react";
import axios from "axios";
import * as XLSX from 'xlsx';
import Constant from "../Components/Utils/Constant";
import { useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebar from "../Components/Sidebar";

export default function GetFeedback({ onClose }) {
  const [isVisible, setIsVisible] = useState(true);
  const [data, setData] = useState([]);
  const [deletePopup, setDeletePopup] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("app");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [previewImg, setPreviewImg] = useState(null);
  const [viewModal, setViewModal] = useState(null); // For viewing feedback details

  // Add useRef for table reference
  const tableRef = useRef(null);

  const feedbackTypes = [
    { value: "app", label: "App" },
    { value: "feature", label: "Feature" },
    { value: "output", label: "Output" },
    { value: "model", label: "Model" },
  ];

  // Fixed Excel export function
  const handleExportToExcel = () => {
    try {
      // Create worksheet from data
      const worksheetData = data.map((item, index) => ({
        'S.No': (currentPage - 1) * 10 + index + 1,
        'Feedback Text': item.text || '',
        'Type': selectedFilter,
        'Status': item.status || 'pending',
        'Created Date': item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '',
        'Has Images': item.feedback_image && item.feedback_image.length > 0 ? 'Yes' : 'No'
      }));

      const ws = XLSX.utils.json_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Feedback Data");
      
      // Generate filename with current filter and date
      const fileName = `feedback_${selectedFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      toast.success("Data exported successfully!");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export data");
    }
  };

  const fetchData = async (page = 1, feedback_type = selectedFilter) => {
    try {
      const response = await fetch(
        `${Constant.BASE_URL}/feedback/get?page=${page}&feedback_type=${feedback_type}`
      );
      const responseData = await response.json();
      if (responseData) {
        setData(responseData.feedbacks || []);
        setCurrentPage(responseData.currentPage || 1);
        setTotalPages(responseData.totalPages || 1);
        setTotalFeedbacks(responseData.totalFeedbacks || 0);
      } else {
        console.error("Error fetching feedback");
      }
    } catch (error) {
      console.error("Error fetching feedback:", error);
    }
  };

  useEffect(() => {
    fetchData(currentPage, selectedFilter);
  }, [deletePopup, selectedFilter, currentPage]);

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = async () => {
    try {
      const response = await axios.delete(
        `${Constant.BASE_URL}/feedback/delete/${deletePopup}`
      );
      if (response.status === 200) {
        toast.success("Feedback deleted Successfully");
        setTimeout(() => {
          setDeletePopup("");
        }, 2000);
      } else {
        console.log("Failed to delete the Feedback:", response.status);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const response = await axios.put(
        `${Constant.BASE_URL}/feedback/${id}/status`,
        { status: newStatus }
      );
      if (response.status === 200) {
        toast.success(`Status updated to ${newStatus}`);
        fetchData(currentPage, selectedFilter); // refresh list
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-blue-100 text-blue-800 border-blue-300",
      inprogress: "bg-yellow-100 text-yellow-800 border-yellow-300",
      completed: "bg-green-100 text-green-800 border-green-300",
    };
    const labels = {
      pending: "Pending",
      inprogress: "In Progress",
      completed: "Completed",
    };
    return (
      <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || "Pending"}
      </span>
    );
  };

  const renderImages = (item) => {
    let images = [];
    if (item.feedback_image && Array.isArray(item.feedback_image)) {
      images = item.feedback_image.slice(0, 3);
    } else if (item.feedback_image) {
      images.push(item.feedback_image);
    }

    return (
      <>
        <div className="flex gap-2 lg:ml-5 xl:ml-20 2xl:ml-[200px]">
          {images.map((image, index) => (
            <div
              key={index}
              className="flex-none rounded-sm cursor-pointer text-center text-black shadow-md"
              onClick={() => setPreviewImg(image)}
            >
              <div className="relative h-16 w-16 overflow-hidden rounded-lg lg:h-20 lg:w-20 xl:h-24 xl:w-24 2xl:h-28 2xl:w-28">
                <img
                  src={image}
                  alt={`feedback-img-${index + 1}`}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                />
              </div>
            </div>
          ))}
        </div>

        {previewImg && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
            onClick={() => setPreviewImg(null)}
          >
            <div className="relative">
              <img
                src={previewImg}
                alt="preview"
                className="max-h-[80vh] max-w-[90vw] rounded-lg shadow-lg"
              />
              <button
                onClick={() => setPreviewImg(null)}
                className="absolute top-2 right-2 rounded-lg bg-red-600 px-3 py-1 text-white"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (currentPage > 1) {
      pages.push(
        <button key="prev" onClick={() => handlePageChange(currentPage - 1)} className="mx-1 rounded bg-gray-200 px-3 py-1 text-gray-700 hover:bg-gray-300">
          Previous
        </button>
      );
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} onClick={() => handlePageChange(i)} className={`mx-1 rounded px-3 py-1 ${currentPage === i ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}>
          {i}
        </button>
      );
    }
    if (currentPage < totalPages) {
      pages.push(
        <button key="next" onClick={() => handlePageChange(currentPage + 1)} className="mx-1 rounded bg-gray-200 px-3 py-1 text-gray-700 hover:bg-gray-300">
          Next
        </button>
      );
    }
    return pages;
  };

  // View Modal Component
  const renderViewModal = () => {
    if (!viewModal) return null;
    const images = Array.isArray(viewModal.feedback_image) ? viewModal.feedback_image : [];

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        onClick={() => setViewModal(null)}
      >
        <div
          className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={() => setViewModal(null)}
            className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>

          {/* Header */}
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800">Feedback Details</h2>
            <div className="mt-2 flex items-center gap-3">
              <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600 capitalize">
                {viewModal.feedback_type}
              </span>
              {getStatusBadge(viewModal.status)}
              {viewModal.createdAt && (
                <span className="text-sm text-gray-400">
                  {new Date(viewModal.createdAt).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </span>
              )}
            </div>
          </div>

          {/* Feedback Text */}
          <div className="mb-4 rounded-lg bg-gray-50 p-4">
            <p className="text-base leading-relaxed text-gray-700 whitespace-pre-wrap">
              {viewModal.text}
            </p>
          </div>

          {/* Images */}
          {images.length > 0 && (
            <div className="mb-4">
              <p className="mb-2 text-sm font-medium text-gray-500">
                Attached Images ({images.length})
              </p>
              <div className="flex gap-3 flex-wrap">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="cursor-pointer overflow-hidden rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                    onClick={() => setPreviewImg(img)}
                  >
                    <img
                      src={img}
                      alt={`attachment-${idx + 1}`}
                      className="h-32 w-32 object-cover transition-transform hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status action */}
          <div className="flex items-center justify-between border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Update Status:</label>
              <select
                value={viewModal.status || "pending"}
                onChange={(e) => {
                  handleUpdateStatus(viewModal._id, e.target.value);
                  setViewModal({ ...viewModal, status: e.target.value });
                }}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="pending">Pending</option>
                <option value="inprogress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <button
              onClick={() => setViewModal(null)}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return isVisible ? (
    <>
      <Sidebar>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="overflow-auto bg-grey-300 p-3 lg:p-3 xl:p-3 2xl:p-6">
          <div
            className="h-[78.55vh] bg-white"
            style={{ borderRadius: "0px 0px 11px 11px" }}
          >
            <div className="flex items-center bg-white px-4 py-3 lg:py-2 xl:py-3 2xl:py-3">
              <div className="flex-1">
                <div className="flex justify-between px-10 lg:w-full lg:px-1">
                  <div className="flex items-center gap-4">
                    <div className="flex cursor-pointer items-center justify-center rounded-sm border border-white">
                      <div className="text-4lg font-bold lg:text-[10px] xl:text-[15px] 2xl:text-[28px]">
                        <p>Feedback </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium lg:text-[8px] xl:text-[12px] 2xl:text-[16px]">
                        Filter:
                      </label>
                      <select
                        value={selectedFilter}
                        onChange={(e) => handleFilterChange(e.target.value)}
                        className="rounded-md border border-gray-300 px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 lg:text-[8px] xl:text-[12px] 2xl:text-[14px]"
                      >
                        {feedbackTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="text-sm text-gray-600 lg:text-[8px] xl:text-[10px] 2xl:text-[14px]">
                      Showing {data.length} of {totalFeedbacks} results
                    </div>
                  </div>
{/*                   
                  Add Export Button
                  <div className="flex items-center">
                    <button
                      onClick={handleExportToExcel}
                      className="rounded-md bg-green-600 px-4 py-2 text-white font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 lg:text-[8px] xl:text-[12px] 2xl:text-[14px] lg:px-2 lg:py-1 xl:px-3 xl:py-1 2xl:px-4 2xl:py-2"
                    >
                      Export to Excel
                    </button>
                  </div> */}
                </div>
              </div>
            </div>

            <div className="overflow-auto lg:h-[58vh] xl:h-[58vh] 2xl:h-[56vh] lg:w-[900px] xl:w-[1410px] 2xl:w-[2400px]">
              {data && data.length > 0 ? (
                data.map((item, i) => (
                  <div
                    // 2. Entire completed row is now faded
                    className={`flex w-full flex-row items-center justify-between border-t-[1px] border-gray-300 px-4 py-3 ${item.status === "completed" ? "opacity-60" : ""}`}
                    key={i}
                  >
                    <div className="flex flex-row items-center gap-5">
                      <div className="text-lg font-semibold">
                        {(currentPage - 1) * 10 + i + 1}.
                      </div>
                      <div className="flex-none border-b-[1px] border-gray-300 pb-1 text-black lg:w-72 xl:w-96 2xl:w-[700px]">
                        <div className="scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-400 max-h-20 overflow-y-auto">
                           {/* 3. Completed text has red line-through */}
                          <p
                            className={`break-words whitespace-pre-wrap px-2 text-left 2xl:text-2xl 
                              ${item.status === "completed" ? "text-red-500 line-through" : ""}`}
                          >
                            {item.text}
                          </p>
                        </div>
                      </div>
                      {renderImages(item)}
                    </div>

                    <div className="flex gap-2">
                      {/* View Button */}
                      <div
                        onClick={() => setViewModal(item)}
                        className="flex w-fit cursor-pointer items-center rounded-lg bg-indigo-500 px-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-600 lg:h-6 lg:px-2 xl:h-10 xl:px-5 xl:text-lg"
                      >
                        View
                      </div>

                      {/* Status Dropdown */}
                      <div className="relative">
                        <select
                          value={item.status || "pending"}
                          onChange={(e) => handleUpdateStatus(item._id, e.target.value)}
                          className={`flex w-fit cursor-pointer items-center rounded-lg px-2 text-sm font-semibold text-white transition-colors lg:h-6 lg:px-2 xl:h-10 xl:px-5 xl:text-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                            (item.status || "pending") === "pending"
                              ? "bg-blue-500 hover:bg-blue-600 focus:ring-blue-500"
                              : (item.status || "pending") === "inprogress"
                              ? "bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500"
                              : "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                          }`}
                        >
                          <option value="pending" className="bg-white text-black">
                            Pending
                          </option>
                          <option value="inprogress" className="bg-white text-black">
                            In Progress
                          </option>
                          <option value="completed" className="bg-white text-black">
                            Completed
                          </option>
                        </select>
                      </div>

                      <div
                        onClick={() => setDeletePopup(item._id)}
                        className="flex w-fit cursor-pointer items-center rounded-lg bg-red-500 px-2 text-sm font-semibold text-white transition-colors hover:bg-red-600 lg:h-6 lg:px-2 xl:h-10 xl:px-5 xl:text-lg"
                      >
                        Delete
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex h-full items-center justify-center">
                  <p className="text-lg text-gray-500">
                    No feedback found for the selected filter.
                  </p>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center border-t border-gray-200 bg-white py-4">
                <div className="flex items-center">{renderPagination()}</div>
                <div className="ml-4 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Popup */}
        {deletePopup && (
          <div className="pop-delete">
            <div className="flex w-full items-center justify-center">
              <div className="w-fit rounded-md bg-white p-4 px-1 py-3 shadow-md">
                <div className="items-center justify-center lg:w-[280px] xl:w-[280px] 2xl:w-[500px]">
                  <div className="mb-4 text-center text-lg font-semibold 2xl:py-3 2xl:text-4xl">
                    Are you sure want to delete this Feedback?
                  </div>
                  <hr className="mt-1 p-2" />
                  <div className="flex flex-col items-center justify-center py-5">
                    <button
                      type="button"
                      onClick={() => setDeletePopup("")}
                      className="flex-shrink-0 w-16 rounded-sm bg-white text shadow-md"
                      style={{ boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.25)" }}
                    >
                      No
                    </button>
                    <button
                      onClick={handleDelete}
                      className="mt-5 w-16 flex-shrink-0 rounded-sm bg-custom-gradient text text-white"
                      style={{ background: "radial-gradient(125% 125% at 85.42% 0%, #FFA6A6 0%, #F00 100%)" }}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Detail Modal */}
        {renderViewModal()}
      </Sidebar>
    </>
  ) : null;
}